/**
 * The one USER_MESSAGE emit. Every send path — new task, continuation,
 * teammate route, retry — goes through it, which is what makes "a send can
 * never fail invisibly" a property of the store rather than of each caller.
 */

import { EVENTS } from "@/constants/events";
import type { getSocket } from "../socketService";
import type { Message } from "../types";

/**
 * The server's ack codes for a message it could not record (its durable inbox append failed).
 * 'message_blocked': a network filter refused the words themselves, so the same text is refused
 * again, and the bubble says what to change; 'message_not_recorded': anything else, so sending
 * again is the advice.
 */
export type NotRecordedCode = NonNullable<Message["sendFailedCode"]>;

/**
 * Connection-truth: every USER_MESSAGE ack is timeout-bounded so a dropped ack
 * surfaces as a visible failure instead of an eternally-pending send. onResult
 * receives the server's ack, or `undefined` for a TRANSPORT failure (no
 * socket, timed-out/errored ack) — delivery UNKNOWN, distinct from a server
 * rejection. Retrying after `undefined` is safe: the server dedups on the
 * client message id (wasRecentlyForwarded / the inbox unique index), so a
 * processed-but-unacked send never runs twice.
 */
const SEND_ACK_TIMEOUT_MS = 10_000;
export type SendAck = {
  success: boolean;
  error?: string;
  /** The server's structured reason for a refusal ('credits_exhausted'); the
   *  client reacts to this, never to the error text. */
  code?: string;
  taskId?: string;
  messageId?: string;
};
/**
 * The ack's not-recorded code, if that is why the send was refused. A send refused this way
 * keeps its task and its words ("Not sent · Retry"): the Retry resends under the same task id,
 * and the server revives the task it marked failed. Read from the structured code only.
 */
export function notRecordedCode(ack?: SendAck): NotRecordedCode | undefined {
  return ack?.code === "message_blocked" || ack?.code === "message_not_recorded"
    ? ack.code
    : undefined;
}

export function emitUserMessage(
  socket: ReturnType<typeof getSocket>,
  payload: Record<string, unknown>,
  onResult: (ack?: SendAck) => void
): void {
  if (!socket) {
    onResult(undefined);
    return;
  }
  if (typeof socket.timeout === "function") {
    socket
      .timeout(SEND_ACK_TIMEOUT_MS)
      .emit(EVENTS.USER_MESSAGE, payload, (err: unknown, ack?: SendAck) => {
        onResult(err ? undefined : ack);
      });
  } else {
    // Unit-test socket fakes without .timeout keep the single-arg ack shape.
    socket.emit(EVENTS.USER_MESSAGE, payload, onResult);
  }
}
